import { SubmissionStatus } from 'enums';
import { messages } from 'i18n';
import theme from 'themes';
import { SubmissionViewMode } from 'broker/components/SubmissionsView/utils';

export interface SubmissionStatusProps {
  color?: string;
  bgColor: string;
  borderColor?: string;
  text: (viewMode: SubmissionViewMode) => string;
  subtitle?: (isDisabled: boolean) => string;
  subtitleColor?: string;
  sortPriority: number;
  preflight: boolean;
  inProgressStatus?: boolean;
  showInBor: boolean;
}

const statusMessages = messages.submission.status;

export const submissionStatusConfig: Record<SubmissionStatus, SubmissionStatusProps> = {
  [SubmissionStatus.Review]: {
    bgColor: theme.palette.grey[100],
    text: () => statusMessages.review,
    sortPriority: 2,
    preflight: true,
    showInBor: false,
  },
  [SubmissionStatus.New]: {
    bgColor: theme.palette.indigo[200],
    text: () => statusMessages.new,
    sortPriority: 3,
    preflight: false,
    showInBor: false,
  },
  [SubmissionStatus.Quoting]: {
    bgColor: theme.palette.lilac[200],
    text: () => statusMessages.quote,
    sortPriority: 4,
    preflight: false,
    showInBor: false,
  },
  [SubmissionStatus.PendingClient]: {
    bgColor: theme.palette.peachOrange[100],
    text: (viewMode) =>
      viewMode === SubmissionViewMode.Retailer ? statusMessages.missingInformation : statusMessages.pendingClient,
    sortPriority: 5,
    preflight: false,
    showInBor: false,
  },
  [SubmissionStatus.Quoted]: {
    bgColor: theme.palette.aquaGreen[100],
    text: () => statusMessages.quoted,
    sortPriority: 6,
    preflight: false,
    showInBor: false,
  },
  [SubmissionStatus.ProposalSent]: {
    bgColor: theme.palette.teal[100],
    text: () => statusMessages.proposalSent,
    sortPriority: 7,
    preflight: false,
    showInBor: false,
  },
  [SubmissionStatus.Binding]: {
    bgColor: theme.palette.turquoise[100],
    text: () => statusMessages.binding,
    sortPriority: 8,
    preflight: false,
    showInBor: false,
  },
  [SubmissionStatus.Issuing]: {
    bgColor: theme.palette.cornflowerBlue[100],
    text: () => statusMessages.issuing,
    sortPriority: 9,
    preflight: false,
    showInBor: true,
  },
  [SubmissionStatus.Issued]: {
    bgColor: theme.palette.teal[200],
    text: () => statusMessages.issued,
    sortPriority: 10,
    preflight: false,
    showInBor: true,
  },
  [SubmissionStatus.Closed]: {
    bgColor: theme.palette.coralRed[100],
    text: () => statusMessages.closed,
    sortPriority: 11,
    preflight: false,
    showInBor: false,
  },
  [SubmissionStatus.Archived]: {
    bgColor: theme.palette.coralRed[100],
    text: () => statusMessages.archived,
    sortPriority: 12,
    preflight: false,
    showInBor: false,
  },
  [SubmissionStatus.Processing]: {
    bgColor: theme.palette.grey[100],
    text: () => statusMessages.processing,
    sortPriority: 100,
    preflight: true,
    inProgressStatus: true,
    subtitle: (isDisabled) => (isDisabled ? '' : messages.submission.reviewSubtitle),
    subtitleColor: 'text.secondary',
    showInBor: false,
  },
  [SubmissionStatus.PendingRenewal]: {
    bgColor: theme.palette.grey[100],
    text: () => statusMessages.pendingRenewal,
    sortPriority: 200,
    preflight: false,
    showInBor: false,
  },
};

export const visibleSubmissionStatuses = Object.keys(SubmissionStatus)
  .map((statusName) => statusName as SubmissionStatus)
  .filter((status) => !submissionStatusConfig[status].inProgressStatus)
  .sort((a, b) => submissionStatusConfig[a].sortPriority - submissionStatusConfig[b].sortPriority);
